import { observable, action, computed } from 'mobx'
import { computedFn } from 'mobx-utils'
import { RootStore, EDITOR_SHOTS_STORE, EDITOR_GROUPS_STORE } from 'store'
import { numberOfThumbnailsToView, defaultThumbObject, Thumbnail, TimelineTabs } from './types'

export const EDITOR_TIMELINE_STORE = 'EDITOR_TIMELINE_STORE'

export class EditorTimelineStore {
  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable timelineSyncedToMonitor: boolean = true
  @observable skipThumbnails: number = 0
  @observable selectedTab: TimelineTabs = TimelineTabs.AI_FRAME_VIEW
  @observable currentMonitorThumbnailIndex: number = 0
  @observable playIntervalId: NodeJS.Timeout = null

  @computed get lastThumbnailIndex () {
    return this.allThumbnailsOfSelectedGroups.length - 1
  }

  @computed get isCurrentMonitorThumbnailIndexLast () {
    return this.currentMonitorThumbnailIndex === this.lastThumbnailIndex
  }

  @computed get allThumbnailsOfSelectedGroups () {
    const allThumbnailsOfSelectedGroups: Thumbnail[] = []

    this.rootStore[EDITOR_SHOTS_STORE].shotsOfSelectedGroups
      .forEach((shot, index) => {
        const { startFrame, endFrame } = shot
        const isEven = Boolean(index % 2)
        const title = `${shot.name || 'ungrouped shots'} (shot ${shot.shotNumberInGroup}/${shot.totalShotsInGroup})`

        for (let i = startFrame; i <= endFrame; i++) {
          allThumbnailsOfSelectedGroups.push({ frame: i, isEven, title, shot })
        }
      })

    return allThumbnailsOfSelectedGroups
  }

  @computed get allFramesOfSelectedGroups () {
    return this.allThumbnailsOfSelectedGroups.map(t => t.frame)
  }

  @computed get timelineThumbnails () {
    if (
      this.selectedTab === TimelineTabs.AI_FRAME_VIEW
    ) return this.allThumbnailsOfSelectedGroups

    const timelineThumbnails: Thumbnail[] = []

    this.rootStore[EDITOR_SHOTS_STORE].shotsOfSelectedGroups
      .forEach((shot, index) => {
        const { startFrame, endFrame } = shot
        const isEven = Boolean(index % 2)
        const title = `${shot.name || 'ungrouped shots'} (shot ${shot.shotNumberInGroup}/${shot.totalShotsInGroup})`

        switch (this.selectedTab) {
          case TimelineTabs.AUTO_CYCLE_VIEW: {
            const numberOfFramesInTheShot = endFrame - startFrame
            switch (true) {
              case (numberOfFramesInTheShot < 10):
                timelineThumbnails.push({ frame: startFrame, isEven, title, shot })
                timelineThumbnails.push({ frame: endFrame, isEven, title, shot })
                break

              case (numberOfFramesInTheShot < 24):
                for (let i = startFrame; i < endFrame; i += 5) {
                  timelineThumbnails.push({ frame: i, isEven, title, shot })
                }
                timelineThumbnails.push({ frame: endFrame, isEven, title, shot })
                break

              case (numberOfFramesInTheShot < 34):
                for (let i = startFrame; i < endFrame; i += 9) {
                  timelineThumbnails.push({ frame: i, isEven, title, shot })
                }
                timelineThumbnails.push({ frame: endFrame, isEven, title, shot })
                break

              case (numberOfFramesInTheShot > 35):
                for (let i = startFrame; i < endFrame; i += 10) {
                  timelineThumbnails.push({ frame: i, isEven, title, shot })
                }
                timelineThumbnails.push({ frame: endFrame, isEven, title, shot })
                break
              default: break
            }
            break
          }

          case TimelineTabs.IN_OUT_VIEW: {
            timelineThumbnails.push({ frame: startFrame, isEven, title, shot })
            timelineThumbnails.push({ frame: endFrame, isEven, title, shot })
            break
          }

          default: break
        }
      })

    return timelineThumbnails
  }

  @computed get timelineFrames () {
    return this.timelineThumbnails.map(t => t.frame)
  }

  @computed get timeLineThumbnailsNumber () {
    return this.timelineThumbnails.length
  }

  @computed get timelineThumbnailsToView () {
    const timelineThumbnailsToView: Thumbnail[] =
      Array(numberOfThumbnailsToView).fill(defaultThumbObject)
    for (let i = 0; i < timelineThumbnailsToView.length; i++) {
      timelineThumbnailsToView[i] = this.timelineThumbnails[i + this.skipThumbnails] ||
        defaultThumbObject
    }
    return timelineThumbnailsToView
  }

  @computed get thumbnailsViewablePercentage () {
    return Math.floor(numberOfThumbnailsToView / this.timelineThumbnails.length * 100)
  }

  @computed get currentMonitorThumbnail () {
    return this.allThumbnailsOfSelectedGroups[this.currentMonitorThumbnailIndex]
  }

  @computed get currentMonitorFrame () {
    return this.currentMonitorThumbnail
      ? this.currentMonitorThumbnail.frame
      : null
  }

  @computed get currentMonitorShot () {
    return this.currentMonitorThumbnail
      ? this.currentMonitorThumbnail.shot
      : null
  }

  @computed get currentMonitorShotId () {
    return this.currentMonitorShot
      ? this.currentMonitorShot.id
      : null
  }

  @computed get currentMonitorGroupName () {
    return this.currentMonitorShot
      ? this.currentMonitorShot.name
      : null
  }

  @computed get currentMonitorGroupShots () {
    if (!this.currentMonitorGroupName) return []

    const {
      [EDITOR_SHOTS_STORE]: { shotsPerGroup }
    } = this.rootStore

    const currentMonitorGroupShots = shotsPerGroup(this.currentMonitorGroupName)

    return currentMonitorGroupShots
  }

  @computed get currentMonitorShotInfo () {
    if (!this.currentMonitorGroupName) return 'Ungrouped shot'

    const shotNumber = this.currentMonitorGroupShots
      .findIndex(s => s.id === this.currentMonitorShotId) + 1

    return `${this.currentMonitorGroupName} 
    (shot ${shotNumber}/${this.currentMonitorGroupShots.length})`
  }

  @computed get currentMonitorShotLocation () {
    return this.currentMonitorShot
      ? this.currentMonitorShot.location
      : null
  }

  @computed get isPlaying () {
    return this.playIntervalId !== null
  }

  @observable isFrameAmongSelected = computedFn((frameNumber: number) => {
    return this.allFramesOfSelectedGroups.includes(frameNumber)
  })

  @action.bound setSkipThumbnails (num: number) {
    const maxValue = this.timelineThumbnails.length - numberOfThumbnailsToView
    const minValue = 0

    switch (true) {
      case (num < minValue):
        this.skipThumbnails = minValue
        break

      case (num > maxValue):
        this.skipThumbnails = maxValue
        break

      default:
        this.skipThumbnails = num
        break
    }
  }

  @action.bound setSelectedTab (value: TimelineTabs) {
    this.selectedTab = value
  }

  @action.bound setCurrentMonitorThumbnailIndex (value: number) {
    this.currentMonitorThumbnailIndex = value

    if (this.timelineSyncedToMonitor) {
      let currentViewIndex = 0
      switch (this.selectedTab) {
        case TimelineTabs.AI_FRAME_VIEW:
          currentViewIndex = this.currentMonitorThumbnailIndex
          break
        case TimelineTabs.AUTO_CYCLE_VIEW:
          currentViewIndex = [
            ...this.timelineThumbnails.map(t => t.frame),
            this.currentMonitorFrame
          ]
            .sort((a, b) => a - b)
            .findIndex(frame => frame === this.currentMonitorFrame)
          break
        case TimelineTabs.IN_OUT_VIEW:
          currentViewIndex = this.timelineThumbnails
            .findIndex(t => t.shot.id === this.currentMonitorShotId)
          break
      }

      this.setSkipThumbnails(currentViewIndex - 1)
    }
  }

  @action.bound play () {
    const lastThumbIndex = this.allThumbnailsOfSelectedGroups.length - 1
    if (
      this.currentMonitorThumbnailIndex > lastThumbIndex
    ) {
      this.setCurrentMonitorThumbnailIndex(Math.max(lastThumbIndex, 0))
      return
    }

    if (this.playIntervalId !== null) return

    this.playIntervalId = setInterval(
      () => this.incrementCurrentMonitorThumbnailIndex(),
      100
    )
  }

  @action.bound stop () {
    clearInterval(this.playIntervalId)
    this.playIntervalId = null
  }

  @action.bound switchTimelineSyncingToMonitor () {
    this.timelineSyncedToMonitor = !this.timelineSyncedToMonitor
  }

  @action.bound clearEditorTimelineState () {
    this.skipThumbnails = 0
    this.currentMonitorThumbnailIndex = 0
    this.playIntervalId = null
  }

  goToFrame = (frameNumber: number) => {
    !this.isFrameAmongSelected(frameNumber) &&
      this.rootStore[EDITOR_GROUPS_STORE].switchAllGroupSelection(false)
    this.setCurrentMonitorFrame(frameNumber)
  }

  setCurrentMonitorFrame = (frameNumber: number) => {
    const index = this.allThumbnailsOfSelectedGroups
      .findIndex(t => t.frame === frameNumber)

    this.setCurrentMonitorThumbnailIndex(index || 0)
  }

  incrementCurrentMonitorThumbnailIndex = (value = 1) => {
    const diff = this.lastThumbnailIndex - this.currentMonitorThumbnailIndex
    let nextIndex = null
    if (diff < value) {
      nextIndex = value - diff - 1
    } else {
      nextIndex = this.currentMonitorThumbnailIndex + value
    }
    this.setCurrentMonitorThumbnailIndex(nextIndex)
  }

  decrementCurrentMonitorThumbnailIndex = (value = 1) => {
    const diff = this.currentMonitorThumbnailIndex
    let nextIndex = null
    if (diff < value) {
      nextIndex = this.lastThumbnailIndex - (value - diff - 1)
    } else {
      nextIndex = this.currentMonitorThumbnailIndex - value
    }
    this.setCurrentMonitorThumbnailIndex(nextIndex)
  }

  goForwardsToTimeline = () => {
    if (!this.timelineSyncedToMonitor) {
      this.setSkipThumbnails(this.skipThumbnails + 1)
      return
    }
    switch (this.selectedTab) {
      case TimelineTabs.AI_FRAME_VIEW:
        this.incrementCurrentMonitorThumbnailIndex()
        break

      case TimelineTabs.AUTO_CYCLE_VIEW:
      case TimelineTabs.IN_OUT_VIEW: {
        const thumbIndex = Math.min(this.skipThumbnails + 3, this.timeLineThumbnailsNumber - 1)
        if (!this.timelineThumbnails[thumbIndex]) break

        const frameNumber = this.timelineThumbnails[thumbIndex].frame

        this.setCurrentMonitorFrame(frameNumber)
        break
      }
    }
  }

  goBackwardsToTimeline = () => {
    if (!this.timelineSyncedToMonitor) {
      this.setSkipThumbnails(this.skipThumbnails - 1)
      return
    }
    switch (this.selectedTab) {
      case TimelineTabs.AI_FRAME_VIEW:
        this.decrementCurrentMonitorThumbnailIndex()
        break

      case TimelineTabs.AUTO_CYCLE_VIEW:
      case TimelineTabs.IN_OUT_VIEW: {
        const thumbIndex = Math.max(this.skipThumbnails - 3, 0)
        if (!this.timelineThumbnails[thumbIndex]) break

        const frameNumber = this.timelineThumbnails[thumbIndex].frame

        this.setCurrentMonitorFrame(frameNumber)
        break
      }
    }
  }

  onScrollBarDragStop = () => {
    if (!this.timelineSyncedToMonitor) return

    const index = Math.min(3, this.timelineThumbnailsToView.length)
    const frameNumber = this.timelineThumbnailsToView[index].frame

    this.setCurrentMonitorFrame(frameNumber)
  }
}
